import { createSignal, onMount } from 'solid-js';

export type SwState = 'unsupported' | 'unregistered' | ServiceWorkerState;

export interface ServiceWorkerInfo {
  registered: () => boolean;
  state: () => SwState;
  scope: () => string | null;
  scriptURL: () => string | null;
  /** Number of registered controllers / clients (informational). */
  refresh: () => Promise<void>;
}

/** Reactive view onto `navigator.serviceWorker` registration state. */
export function createServiceWorkerInfo(): ServiceWorkerInfo {
  const supported = typeof navigator !== 'undefined' && 'serviceWorker' in navigator;
  const [registered, setRegistered] = createSignal(false);
  const [state, setState] = createSignal<SwState>(supported ? 'unregistered' : 'unsupported');
  const [scope, setScope] = createSignal<string | null>(null);
  const [scriptURL, setScriptURL] = createSignal<string | null>(null);

  const refresh = async () => {
    if (!supported) return;
    const reg = await navigator.serviceWorker.getRegistration();
    if (!reg) {
      setRegistered(false);
      setState('unregistered');
      setScope(null);
      setScriptURL(null);
      return;
    }
    setRegistered(true);
    setScope(reg.scope);
    const worker = reg.active ?? reg.waiting ?? reg.installing;
    if (worker) {
      setState(worker.state);
      setScriptURL(worker.scriptURL);
      worker.addEventListener('statechange', () => setState(worker.state));
    } else {
      setState('unregistered');
      setScriptURL(null);
    }
  };

  onMount(() => {
    void refresh();
  });

  return { registered, state, scope, scriptURL, refresh };
}
